import React, { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import trim from "lodash/trim";
import split from "lodash/split";
import uniq from "lodash/uniq";
import isEmpty from "lodash/isEmpty";
import isArray from "lodash/isArray";
import last from "lodash/last";
import slice from "lodash/slice";
import i18n from "i18n";
import LicenseCheck from "license/LicenseCheck";
import UserNotification from "util/UserNotification";
import { Button, ButtonToolbar, Col, Row, Table, Input } from "components/bootstrap";
import { PaginatedList, SearchForm, TimeUnitInput, Timestamp } from "components/common";
import {
  MONGODB_DATA_ADAPTER_QUERY_KEY,
  useMongoDBDataAdapterEntries,
  createMongoDBDataAdapterEntry,
  updateMongoDBDataAdapterEntry,
  deleteMongoDBDataAdapterEntry
} from "../hooks/useMongoDBDataAdapter";
import MongoDBDataAdapterEntry from "../models/MongoDBDataAdapterEntry";
const DEFAULT_TTL = { value: 1, unit: "", enabled: false };
const _errorHandler = (message, title) => (error) => {
  let errorMessage;
  try {
    if (isArray(error.additional.body)) {
      const invalidFields = error.additional.body.map((e) => last(split(e.path, "."))).join(", ");
      errorMessage = i18n.t("mongodb_table.invalid_fields", { ns: "enterprise_lookup", fields: invalidFields });
    } else {
      errorMessage = error.additional.body.message;
    }
  } catch (_e) {
    errorMessage = error.message;
  }
  UserNotification.error(`${message}: ${errorMessage}`, title);
};
const trimValues = (values) => uniq(values.map((value) => trim(value)).filter((value) => (value != null ? value : "").length > 0));
const MongoDBDataAdapterTable = ({ dataAdapterId }) => {
  var _a;
  const { t } = useTranslation("enterprise_lookup");
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [storeId, setStoreId] = useState("");
  const [storeKey, setStoreKey] = useState("");
  const [storeValues, setStoreValues] = useState([]);
  const [ttl, setTtl] = useState(DEFAULT_TTL);
  const [editMode, setEditMode] = useState(false);
  const { data } = useMongoDBDataAdapterEntries({ dataAdapterId, query, page, perPage });
  const pagination = data == null ? void 0 : data.pagination;
  const entries = (_a = data == null ? void 0 : data.entries) != null ? _a : [];
  const reloadEntries = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [...MONGODB_DATA_ADAPTER_QUERY_KEY, "entries"] }),
    [queryClient]
  );
  const clearValues = useCallback(() => {
    setStoreId("");
    setStoreValues([]);
    setStoreKey("");
    setEditMode(false);
    setTtl({ value: 1, unit: "", enabled: false });
  }, []);
  const clearValuesAndReloadEntries = useCallback(() => {
    clearValues();
    reloadEntries();
  }, [clearValues, reloadEntries]);
  const handleSearch = useCallback((newQuery) => {
    setQuery(newQuery);
    setPage(1);
  }, []);
  const handleSearchReset = useCallback(() => {
    setQuery("");
    setPage(1);
  }, []);
  const handlePageChange = useCallback((newPage, newPerPage) => {
    setPage(newPage);
    setPerPage(newPerPage);
  }, []);
  const addEntry = useCallback(
    (e) => {
      var _a2, _b;
      (_a2 = e == null ? void 0 : e.preventDefault) == null ? void 0 : _a2.call(e);
      (_b = e == null ? void 0 : e.stopPropagation) == null ? void 0 : _b.call(e);
      const newEntryBuilder = MongoDBDataAdapterEntry.builder().dataAdapterId(dataAdapterId).id(isEmpty(trim(storeId)) ? null : storeId).key(storeKey).values(trimValues(storeValues)).ttl(ttl.enabled ? ttl.value : null).ttl_unit(ttl.enabled ? ttl.unit : null);
      if (editMode) {
        updateMongoDBDataAdapterEntry(newEntryBuilder.build()).then(clearValuesAndReloadEntries).catch(_errorHandler(t("mongodb_table.cannot_update_entry"), t("mongodb_table.update_failed")));
      } else {
        createMongoDBDataAdapterEntry(newEntryBuilder.build()).then(clearValuesAndReloadEntries).catch(_errorHandler(t("mongodb_table.cannot_create_entry"), t("mongodb_table.creation_failed")));
      }
    },
    [clearValuesAndReloadEntries, dataAdapterId, editMode, storeId, storeKey, storeValues, t, ttl]
  );
  const resetForm = useCallback(() => {
    clearValues();
  }, [clearValues]);
  const editEntry = useCallback(
    (entry) => () => {
      setStoreId(entry.id);
      setStoreKey(entry.key);
      setStoreValues(entry.values);
      setTtl({ value: entry.ttl, unit: entry.ttlUnit, enabled: entry.ttl !== null });
      setEditMode(true);
    },
    []
  );
  const removeEntry = useCallback(
    (entry) => () => {
      deleteMongoDBDataAdapterEntry(entry).then(reloadEntries).catch(_errorHandler(t("mongodb_table.cannot_delete_entry"), t("mongodb_table.deletion_failed")));
    },
    [reloadEntries, t]
  );
  const updateTTL = useCallback((value, unit, enabled) => {
    if (enabled && value) {
      setTtl({ value, unit, enabled });
    } else {
      setTtl({ value: null, unit: null, enabled: false });
    }
  }, []);
  const entryList = entries.map((entry) => {
    const key = entry.id;
    const values = entry.values || [];
    const expires = entry.expire_after || null;
    const valueList = slice(values, 0, 5).map((value) => /* @__PURE__ */ React.createElement("li", { key: `value-list-item-${value}` }, '"', value, '"'));
    if (values.length > 5) {
      valueList.push(
        /* @__PURE__ */ React.createElement("li", { key: "truncated" }, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "mongodb_table.values_truncated", components: { emphasis: /* @__PURE__ */ React.createElement("em", null) } }))
      );
    }
    return /* @__PURE__ */ React.createElement("tr", { key }, /* @__PURE__ */ React.createElement("td", null, entry.key), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement("ol", { style: { paddingInlineStart: "1.5em" } }, valueList)), /* @__PURE__ */ React.createElement("td", null, expires ? /* @__PURE__ */ React.createElement(Timestamp, { dateTime: expires }) : t("mongodb_table.does_not_expire")), /* @__PURE__ */ React.createElement("td", { style: { width: "8.5em" } }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { id: `edit-entry-${key}`, onClick: editEntry(entry), bsSize: "xsmall", bsStyle: "info" }, t("mongodb_table.edit")), /* @__PURE__ */ React.createElement(Button, { id: `remove-entry-${key}`, onClick: removeEntry(entry), bsSize: "xsmall", bsStyle: "danger" }, t("mongodb_table.delete")))));
  });
  if (dataAdapterId === "" || !dataAdapterId) {
    return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", null, t("mongodb_table.entries_not_addable")));
  }
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    LicenseCheck,
    {
      displayWarningContainer: true,
      bsStyle: "danger",
      title: t("mongodb_table.license_disabled_title"),
      text: t("mongodb_table.license_disabled_text")
    }
  ), /* @__PURE__ */ React.createElement(Row, { style: { marginBottom: 20 } }, /* @__PURE__ */ React.createElement("h3", { style: { marginBottom: 10 } }, t("mongodb_table.create_update_heading")), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: "lookup-key",
      label: t("mongodb_table.key"),
      value: storeKey,
      placeholder: t("mongodb_table.key_placeholder"),
      help: t("mongodb_table.key_help"),
      labelClassName: "col-sm-1",
      wrapperClassName: "col-sm-11",
      onChange: (event) => setStoreKey(trim(event.target.value))
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "textarea",
      id: "lookup-key-values",
      rows: 5,
      label: t("mongodb_table.values"),
      value: (storeValues || []).join("\n"),
      placeholder: t("mongodb_table.values_placeholder"),
      help: t("mongodb_table.values_help"),
      labelClassName: "col-sm-1",
      wrapperClassName: "col-sm-11",
      onChange: (event) => setStoreValues(split(event.target.value, /\r?\n/))
    }
  ), /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: t("mongodb_table.ttl_label"),
      update: updateTTL,
      value: ttl.value,
      help: t("mongodb_table.ttl_help"),
      unit: ttl.unit || "MINUTES",
      units: ["MILLISECONDS", "SECONDS", "MINUTES", "HOURS", "DAYS"],
      enabled: ttl.enabled,
      labelClassName: "col-sm-1",
      wrapperClassName: "col-sm-11"
    }
  ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { smOffset: 1, sm: 11 }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(
    Button,
    {
      id: "submit-entry",
      disabled: isEmpty(trimValues(storeValues)) || storeKey === "",
      type: "button",
      onClick: addEntry,
      bsSize: "small",
      bsStyle: "primary"
    },
    editMode ? t("mongodb_table.update_entry") : t("mongodb_table.add_new_entry")
  ), /* @__PURE__ */ React.createElement(Button, { id: "clear-entry", onClick: resetForm, bsSize: "small" }, t("mongodb_table.clear_form")))))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement("h3", null, t("mongodb_table.configured_heading")), /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      onChange: handlePageChange,
      activePage: pagination == null ? void 0 : pagination.page,
      totalItems: pagination == null ? void 0 : pagination.total,
      pageSize: pagination == null ? void 0 : pagination.perPage,
      pageSizes: [10, 50, 100],
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(SearchForm, { onSearch: handleSearch, onReset: handleSearchReset, topMargin: 10 }),
    /* @__PURE__ */ React.createElement(Table, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, t("mongodb_table.key")), /* @__PURE__ */ React.createElement("th", null, t("mongodb_table.values")), /* @__PURE__ */ React.createElement("th", null, t("mongodb_table.expires_at")), /* @__PURE__ */ React.createElement("th", null, t("mongodb_table.action")))), /* @__PURE__ */ React.createElement("tbody", null, entryList))
  )));
};
export default MongoDBDataAdapterTable;
